import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { InputNumber } from "@/components/InputNumber";
import { datePickerFormItemProps, datePickerStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { validations } from "@/utils/validationUtils";
import { Card, Checkbox, Col, DatePicker, Descriptions, Divider, Form, Input, Row, Select } from "antd";
import { Rule } from "antd/lib/form";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import t from "../../../../../../../app/i18n";
import { rowGutter } from "../../../../../../../common/constants";
import { ClientType } from "../../../../../../client/enums";
import { PaymentFrequency } from "../../../../../../contract/enums";
import { LoanReinsuranceAmount, RealtyComplicityAmount } from "../../../enums";
import { RealtyCalc } from "../../../types";

interface Props {
  policyHolderType?: ClientType;
}

const colSpan = 4;

const vinculationRule = (realtyInsuranceEnabled: boolean): Rule => ({
  validator: (_, value) =>
    value && !realtyInsuranceEnabled ? Promise.reject(t("calc.realty.validations.vinculation")) : Promise.resolve(),
});

export const RealtyCalcInsuranceSection = ({ policyHolderType }: Props) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance<RealtyCalc>();
  const rentReinsurance = Form.useWatch(["generalInsuranceData", "rentReinsurance"], form);
  const loanReinsurance = Form.useWatch(["generalInsuranceData", "loanReinsurance"], form);
  const complicity = Form.useWatch(["generalInsuranceData", "complicity"], form);
  const realtyInsuranceEnabled = Form.useWatch(["realtyInsuranceEnabled"], form);

  const beginningDateMin = dayjs();
  const beginningDateMax = dayjs().add(1, "year").add(6, "week");

  return (
    <Card type="inner" className="card-box" title={t("calc.realty.sections.otherDataReinsurancesAndCrossSelling")}>
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={["generalInsuranceData", "effectiveBeginningDate"]}
            label={t("calc.realty.attrs.generalInsuranceData.effectiveBeginningDate")}
            rules={[validations.notNull, validations.dateInInterval(beginningDateMin, beginningDateMax)]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} minDate={beginningDateMin} maxDate={beginningDateMax} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["generalInsuranceData", "paymentFrequency"]}
            label={t("contract.enums.paymentFrequency._label")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={[PaymentFrequency.ANNUALLY, PaymentFrequency.SEMI_ANNUALLY, PaymentFrequency.QUARTERLY].map(
                (frequency) => ({
                  value: frequency,
                  label: t("contract.enums.paymentFrequency." + frequency),
                }),
              )}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan} offset={colSpan}>
          <Form.Item
            name={["generalInsuranceData", "vinculation"]}
            className="form-item-without-label"
            valuePropName="checked"
            dependencies={["realtyInsuranceEnabled"]}
            rules={[vinculationRule(!!realtyInsuranceEnabled)]}
            initialValue={false}
          >
            <Checkbox>{t("calc.realty.attrs.generalInsuranceData.vinculation")}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["generalInsuranceData", "complicity"]}
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox>{t("calc.realty.attrs.generalInsuranceData.complicity")}</Checkbox>
          </Form.Item>
        </Col>

        {complicity && (
          <Col span={colSpan}>
            <Form.Item
              name={["generalInsuranceData", "complicityAmount"]}
              label={
                <LabelWithPopover
                  label={t("calc.realty.enums.complicityAmount._label")}
                  popoverTitle={t("calc.realty.helpers.complicityDesc.title")}
                  popoverContent={
                    <table className="data-table-view wide-margins">
                      <tbody>
                        {[1, 2, 3, 4, 5, 6, 7].map((value) => (
                          <tr key={value}>
                            <td>{t("calc.realty.helpers.complicityDesc.institution" + value)}</td>
                            <td>{t("calc.realty.helpers.complicityDesc.value" + value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  }
                />
              }
              rules={[validations.notNull]}
            >
              <Select
                {...selectStandardProps}
                options={Object.keys(RealtyComplicityAmount).map((amount) => ({
                  value: amount,
                  label: t("calc.realty.enums.complicityAmount." + amount),
                }))}
              />
            </Form.Item>
          </Col>
        )}
      </Row>

      <Row gutter={rowGutter}>
        <Col span={12}>
          <Divider className="divider-subheader">{t("calc.realty.sections.reinsurances")}</Divider>

          <Row gutter={rowGutter}>
            <Col span={24}>
              <Form.Item
                name={["generalInsuranceData", "cyberneticReinsurance"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>
                  <LabelWithTooltip
                    label={t("calc.realty.attrs.generalInsuranceData.cyberneticReinsurance")}
                    tooltip={t("calc.realty.helpers.cyberneticReinsuranceDesc")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={24}>
              <Form.Item
                name={["generalInsuranceData", "warrantyReinsurance"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>
                  <LabelWithTooltip
                    label={t("calc.realty.attrs.generalInsuranceData.warrantyReinsurance")}
                    tooltip={t("calc.realty.helpers.warrantyReinsuranceDesc")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={24}>
              <Form.Item
                name={["generalInsuranceData", "cyclingReinsurance"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>
                  <LabelWithTooltip
                    label={t("calc.realty.attrs.generalInsuranceData.cyclingReinsurance")}
                    tooltip={t("calc.realty.helpers.cyclingReinsuranceDesc")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={12}>
              <Form.Item
                name={["generalInsuranceData", "loanReinsurance"]}
                className={loanReinsurance ? "form-item-without-label" : undefined}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox style={loanReinsurance ? { maxHeight: 22 } : undefined} disabled={!realtyInsuranceEnabled}>
                  <LabelWithTooltip
                    tooltip={t("calc.realty.helpers.loanReinsuranceDesc")}
                    label={t("calc.realty.attrs.generalInsuranceData.loanReinsurance")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>

            {loanReinsurance && (
              <Col span={8}>
                <Form.Item
                  name={["generalInsuranceData", "loanReinsuranceAmount"]}
                  label={t("calc.realty.enums.loanReinsuranceAmount._label")}
                  rules={[validations.notNull]}
                >
                  <Select
                    {...selectStandardProps}
                    options={Object.keys(LoanReinsuranceAmount).map((amount) => ({
                      value: amount,
                      label: t("calc.realty.enums.loanReinsuranceAmount." + amount),
                    }))}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={rowGutter}>
            <Col span={12}>
              <Form.Item
                name={["generalInsuranceData", "rentReinsurance"]}
                className={rentReinsurance ? "form-item-without-label" : undefined}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox style={rentReinsurance ? { maxHeight: 22 } : undefined}>
                  <LabelWithTooltip
                    tooltip={t("calc.realty.helpers.rentReinsuranceDesc")}
                    label={t("calc.realty.attrs.generalInsuranceData.rentReinsurance")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>

            {rentReinsurance && (
              <Col span={8}>
                <Form.Item
                  name={["generalInsuranceData", "rentReinsuranceAmount"]}
                  label={
                    <LabelWithTooltip
                      tooltip={t("calc.realty.helpers.rentReinsuranceAmountDesc")}
                      label={t("calc.realty.attrs.generalInsuranceData.rentReinsuranceAmount")}
                    />
                  }
                  rules={[
                    validations.notNull,
                    validations.minNumber(1),
                    validations.maxNumber(24_000),
                    validations.multipleOf100,
                  ]}
                >
                  <InputNumber addon="euro" step={100} />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={rowGutter}>
            <Col span={24}>
              <Form.Item
                name={["generalInsuranceData", "allRiskReinsurance"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>
                  <LabelWithPopover
                    label={t("calc.realty.attrs.generalInsuranceData.allRiskReinsurance")}
                    popoverContent={
                      <Descriptions
                        size="small"
                        column={1}
                        style={{ width: 500 }}
                        bordered
                        items={[
                          {
                            label: t("calc.realty.helpers.allRiskReinsuranceDesc.institution1"),
                            children: t("calc.realty.helpers.allRiskReinsuranceDesc.value1"),
                          },
                          {
                            label: t("calc.realty.helpers.allRiskReinsuranceDesc.institution2"),
                            children: (
                              <>
                                {t("calc.realty.helpers.allRiskReinsuranceDesc.value2.text")}
                                <ul style={{ marginBottom: 0 }}>
                                  {[1, 2, 3].map((value) => (
                                    <li key={value}>
                                      {t("calc.realty.helpers.allRiskReinsuranceDesc.value2.item" + value)}
                                    </li>
                                  ))}
                                </ul>
                              </>
                            ),
                          },
                        ]}
                      />
                    }
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={12}>
          <Divider className="divider-subheader">{t("calc.realty.sections.crossSelling")}</Divider>

          <Row gutter={rowGutter}>
            <Col span={8}>
              <Form.Item
                name={["generalInsuranceData", "crossSelling", "uniqaContractNumber"]}
                label={t("calc.realty.attrs.generalInsuranceData.crossSelling.uniqaContractNumber")}
                rules={[validations.size(1, 64)]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={["generalInsuranceData", "crossSelling", "premiumContractNumber"]}
                label={t("calc.realty.attrs.generalInsuranceData.crossSelling.premiumContractNumber")}
                rules={[validations.size(1, 64)]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={["generalInsuranceData", "crossSelling", "colonnadeDiscountCode"]}
                label={t("calc.realty.attrs.generalInsuranceData.crossSelling.colonnadeDiscountCode")}
                rules={[validations.size(1, 64)]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={8}>
              <Form.Item
                name={["generalInsuranceData", "crossSelling", "csobContract"]}
                valuePropName="checked"
                className="form-item-without-label"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>{t("calc.realty.attrs.generalInsuranceData.crossSelling.csobContract")}</Checkbox>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={["generalInsuranceData", "crossSelling", "generaliContract"]}
                valuePropName="checked"
                className="form-item-without-label"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>{t("calc.realty.attrs.generalInsuranceData.crossSelling.generaliContract")}</Checkbox>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={["generalInsuranceData", "crossSelling", "unionHealthContract"]}
                valuePropName="checked"
                className="form-item-without-label"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={policyHolderType !== ClientType.NATURAL}>
                  {t("calc.realty.attrs.generalInsuranceData.crossSelling.unionHealthContract")}
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );
};
